import type {
  CtaBannerContent,
  FaqContent,
  TestimonialGridContent,
  TextSectionContent,
} from '../types';
import {
  defaultCtaBanner as defaultCtaBannerEn,
  defaultTestimonials as defaultTestimonialsEn,
  investorsSection as investorsSectionEn,
  safeSpaceSection as safeSpaceSectionEn,
  trustedBySection as trustedBySectionEn,
} from '../shared';

/**
 * Arabic copy for the shared marketing sections, from the nsave Arabic
 * Localization Pack (source snapshot 31 July 2026).
 *
 * The user-count, country-count and rating figures below are still literal text
 * inherited from the English copy. Checklist item 3 of the pack requires them to
 * come from a single controlled source — see the TODO on `trustedBySection`.
 */

export const trustedBySection: TextSectionContent = {
  ...trustedBySectionEn,
  // TODO(i18n): "500" must come from the same controlled variable as the English
  // heading. `TextSection` keys its odometer animation off the literal token
  // "500", so changing this string without updating COUNT_UP_TOKEN silently
  // disables the count-up rather than breaking the build.
  title: 'أكتر من 500 ألف مستخدم\nحول العالم واثقين في nsave',
  body: 'nsave بيفتح لك باب النظام المالي العالمي حتى لو الخدمات البنكية التقليدية مش بتديك نفس الفرص. تقدر تحتفظ بـUSD وGBP وEUR، وتاخد بيانات حساب باسمك، وتحرك فلوسك دوليًا بسهولة أكبر.',
  link: { ...trustedBySectionEn.link!, label: 'اعرف تبدأ إزاي' },
  publications: {
    ...trustedBySectionEn.publications!,
    alt: 'اتكلمت عننا Financial Times وTechCrunch وFintech Finance News وBloomberg',
  },
};

export const safeSpaceSection: TextSectionContent = {
  ...safeSpaceSectionEn,
  title: 'المكان الآمن\nلفلوسك',
  body: 'أمان فلوسك أولوية عند nsave. أموال العملاء بتتحفظ في حسابات منفصلة عن أموال الشركة، ومش بتُستخدم في تشغيل النشاط. فريق تقني متخصص بيحمي حسابك على مدار الساعة، وفريق دعم حقيقي موجود لما تحتاج مساعدة.',
  link: { ...safeSpaceSectionEn.link!, label: 'عن nsave' },
};

export const investorsSection = {
  ...investorsSectionEn,
  title: 'مدعوم من مستثمرين دعموا شركات عالمية ناجحة',
};

export const defaultTestimonials: TestimonialGridContent = {
  ...defaultTestimonialsEn,
  title: 'معمول لناس زيك',
  // `image.alt` is translated too: it is what a screen reader announces, and what
  // shows if the photo fails to load, so leaving it English puts an English
  // description under an Arabic label.
  items: [
    {
      ...defaultTestimonialsEn.items[0],
      label: 'العاملين عن بُعد',
      image: { ...defaultTestimonialsEn.items[0].image, alt: 'شخص بيشتغل عن بُعد بيستخدم nsave' },
    },
    {
      ...defaultTestimonialsEn.items[1],
      label: 'المستقلين والفريلانسرز',
      image: { ...defaultTestimonialsEn.items[1].image, alt: 'فريلانسر بيستخدم nsave' },
    },
    {
      ...defaultTestimonialsEn.items[2],
      label: 'المسافرين',
      image: { ...defaultTestimonialsEn.items[2].image, alt: 'مسافر بيستخدم nsave' },
    },
  ],
};

export const defaultCtaBanner: CtaBannerContent = {
  ...defaultCtaBannerEn,
  // CtaBanner splits this on the leading "4.4" token to drive its odometer, so
  // the rating has to stay at the start of the string.
  title: '4.4 من 5.0 على App Store وGoogle Play Store',
};

/**
 * Arabic FAQ.
 *
 * ⚠️ NOT FULLY APPROVED, and therefore **not rendered**. The localization pack
 * supplies Arabic for the seven *questions* (section 9) but no Arabic answers.
 * The answers below are adapted from phrasing the pack does approve elsewhere
 * (notably the segregated-funds wording in section 8), but they have not been
 * through the product/compliance review the pack requires for regulatory
 * statements.
 *
 * `AR_COPY_APPROVED.faq` (see `./approval.ts`) is `false`, so `localized.ts`
 * serves the English FAQ on `/ar` instead. Flip it once these are signed off.
 */
export const defaultFaq: FaqContent = {
  title: 'أسئلة شائعة',
  items: [
    {
      question: 'مين يقدر يفتح حساب USD أو GBP أو EUR مع nsave؟',
      answer:
        'nsave معمول للناس في الدول المدعومة اللي عايزة وصول لعملات مستقرة. نزّل التطبيق، أكّد هويتك، وافتح حسابك في دقايق.',
    },
    {
      question: 'هل دي بيانات حساب حقيقية؟',
      answer:
        'أيوه. بتاخد بيانات حساب حقيقية باسمك: بيانات ACH وWire للـUSD، وSort Code ورقم حساب للـGBP، وIBAN للـEUR.',
    },
    {
      question: 'فلوسي آمنة إزاي؟',
      answer:
        'أموال العملاء بتتحفظ في حسابات منفصلة عن أموال الشركة، ومش بتُستخدم في تشغيل النشاط، فهي تفضل ملكك دايمًا.',
    },
    {
      question: 'فتح الحساب بياخد وقت قد إيه؟',
      answer:
        'أغلب العملاء بيخلّصوا التحقق ويكونوا جاهزين في دقايق. في حالات معينة الفحوصات الإضافية ممكن تاخد لحد يوم عمل.',
    },
    {
      question: 'التكلفة كام؟',
      answer:
        'فتح الحساب مجاني. بتدفع رسوم واضحة على التحويل وتحويل العملة، بسعر الصرف الحقيقي من غير فروق مخفية.',
    },
    {
      question: 'أقدر أحتفظ وأحوّل بين أنهي عملات؟',
      answer:
        'تقدر تحتفظ وتحوّل بين USD وGBP وEUR، وتبعت فلوس لبلدك بعملات محلية زي EGP وPKR وBDT وغيرها.',
    },
    {
      question: 'أضيف فلوس للحساب أو أستقبل مدفوعاتي إزاي؟',
      answer:
        'شارك بيانات حسابك مع جهة عملك أو عملائك أو منصات العمل الحر لاستقبال التحويلات، أو اشحن حسابك بعملة مستقرة حسب الخدمات المتاحة في بلدك.',
    },
  ],
};
